// ==============================================
// File: src/lib/drive.js
// Minimal Google Drive adapter using GIS + Drive v3
// Scope: drive.file  (only files the app creates/opens)
// Debug via VITE_DRIVE_DEBUG=true
// FedCM prompt (no popup window)
// Folder-scoped to /Mudo for all file ops
// Hardened: better errors, safe queries, tiny caches, silent init
// ==============================================

const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
const G_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const LS_DRIVE_ROOT_ID = "mudoDriveRootId";

const DEV_STATS = import.meta?.env?.MODE === "development";

function ensureDevStats() {
  if (!DEV_STATS) return null;
  if (typeof window === "undefined") return null;
  const w = window;
  const stats = w.__mudoStorageStats || (w.__mudoStorageStats = {});
  stats.driveRequests =
    stats.driveRequests && typeof stats.driveRequests === "object"
      ? stats.driveRequests
      : { total: 0, list: 0, get: 0, create: 0, update: 0 };
  stats.getFileIdByName =
    stats.getFileIdByName && typeof stats.getFileIdByName === "object"
      ? stats.getFileIdByName
      : { cacheHit: 0, fetch: 0 };
  return stats;
}

function incDriveRequest(type) {
  const stats = ensureDevStats();
  if (!stats) return;
  const dr = stats.driveRequests;
  dr.total = Number(dr.total || 0) + 1;
  if (type && Object.prototype.hasOwnProperty.call(dr, type)) {
    dr[type] = Number(dr[type] || 0) + 1;
  }
}

function incFileIdStat(kind) {
  const stats = ensureDevStats();
  if (!stats) return;
  const g = stats.getFileIdByName;
  if (kind === "cacheHit") g.cacheHit = Number(g.cacheHit || 0) + 1;
  if (kind === "fetch") g.fetch = Number(g.fetch || 0) + 1;
}

// Debug utilities
const DBG = String(import.meta.env.VITE_DRIVE_DEBUG || "").toLowerCase() === "true";
const log  = (...a) => { if (DBG) console.log("[drive]", ...a); };
const warn = (...a) => { if (DBG) console.warn("[drive]", ...a); };

// Token state
let _token = null;
let _tokenExpiry = 0;
let _tokenClient = null;
let _popupInFlight = false;
let _popupPromise = null;

// NEW: Cooldown to prevent repeated background auth attempts
let _lastSilentAuthAttempt = 0;
const SILENT_AUTH_COOLDOWN_MS = 30000; // 30 seconds

const nowSec = () => Math.floor(Date.now() / 1000);
const ts = (sec) => new Date(sec * 1000).toISOString().replace("T", " ").replace("Z", " UTC");

// Small in-memory cache for folder path → id
const _folderCache = new Map();
const LS_FOLDER_CACHE = "mudo_drive_folder_cache_v1";
const FIND_TTL_MS = 2500;
const _findCache = new Map();
const _findInflight = new Map();
const FILE_TTL_MS = 1500;
const _fileCache = new Map();
const _fileInflight = new Map();

// ✅ FIX #3: FileId cache – avoid repeated Drive metadata queries
// Key format: `${parentId}:${name}` → fileId
const _fileIdCache = new Map();
const FILE_ID_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
function loadFolderCache() {
  try {
    const raw = localStorage.getItem(LS_FOLDER_CACHE);
    if (!raw) return;
    const obj = JSON.parse(raw);
    if (obj && typeof obj === "object") {
      Object.entries(obj).forEach(([k, v]) => _folderCache.set(k, v));
    }
  } catch {}
}
function saveFolderCache() {
  try {
    const obj = {};
    for (const [k, v] of _folderCache) obj[k] = v;
    localStorage.setItem(LS_FOLDER_CACHE, JSON.stringify(obj));
  } catch {}
}
loadFolderCache();

/* ---------------- GIS core ---------------- */

export async function ensureGisReady() {
  if (window.google?.accounts?.oauth2) return;
  await new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = res;
    s.onerror = () => rej(new Error("Failed to load Google Identity Services"));
    document.head.appendChild(s);
  });
  log("GIS script loaded / ready.");
}

function ensureTokenClient() {
  if (_tokenClient) return _tokenClient;
  if (!window.google?.accounts?.oauth2) throw new Error("GIS not ready");
  log("initTokenClient()", { clientIdTrunc: (G_CLIENT_ID || "").slice(0, 8) + "…" });
  _tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: G_CLIENT_ID,
    scope: DRIVE_SCOPE,
    callback: () => {}, // replaced per request
  });
  return _tokenClient;
}

/* ---------------- Token flows ---------------- */

export async function ensureTokenSilently() {
  if (!window.google?.accounts?.oauth2) return;
  ensureTokenClient();

  if (_token && nowSec() < _tokenExpiry) return _token;

  // NEW: Cooldown check - prevent repeated failed attempts
  const now = Date.now();
  if (now - _lastSilentAuthAttempt < SILENT_AUTH_COOLDOWN_MS) {
    warn("ensureTokenSilently: in cooldown, rejecting");
    throw new Error("DRIVE_AUTH_COOLDOWN");
  }
  _lastSilentAuthAttempt = now;

  return new Promise((resolve, reject) => {
    _tokenClient.callback = (resp) => {
      if (resp?.access_token) {
        _token = resp.access_token;
        _tokenExpiry = nowSec() + (resp.expires_in || 3600) - 30;
        log("silent token", { willExpireAt: ts(_tokenExpiry) });
        resolve(_token);
      } else {
        warn("silent token failed", resp);
        // NEW: Mark failure for cooldown
        reject(new Error("DRIVE_AUTH_REQUIRED"));
      }
    };
    try {
      _tokenClient.requestAccessToken({ prompt: "", use_fedcm_for_prompt: true });
    } catch (e) {
      // NEW: Catch popup-blocked errors
      warn("ensureTokenSilently caught error:", e.message);
      reject(new Error("DRIVE_AUTH_REQUIRED"));
    }
  });
}

/**
 * Must be called directly in a click handler.
 * Uses FedCM (browser modal) instead of popup windows.
 * Idempotent: second call returns the same pending promise.
 */
export async function requestTokenFromUserGesture() {
  if (!window.google?.accounts?.oauth2) {
    alert("Google sign-in is still loading. Please click again shortly.");
    return Promise.reject(new Error("GIS not ready"));
  }

  if (_popupInFlight && _popupPromise) return _popupPromise;

  ensureTokenClient();
  _popupInFlight = true;

  _popupPromise = new Promise((resolve, reject) => {
    _tokenClient.callback = (resp) => {
      _popupInFlight = false;
      _popupPromise = null;

      if (resp?.access_token) {
        _token = resp.access_token;
        _tokenExpiry = nowSec() + (resp.expires_in || 3600) - 30;
        _lastSilentAuthAttempt = 0; // NEW: Reset cooldown on successful auth
        log("token received", { willExpireAt: ts(_tokenExpiry) });
        resolve(_token);
      } else {
        warn("token missing from GIS response", resp);
        reject(new Error("Google Drive login was cancelled or failed."));
      }
    };

    try {
      _tokenClient.requestAccessToken({
        prompt: "consent",
        use_fedcm_for_prompt: true,
      });
    } catch (e) {
      _popupInFlight = false;
      _popupPromise = null;
      reject(e);
    }
  });

  return _popupPromise;
}

export async function initDriveSilently() {
  try {
    await ensureGisReady();
    await ensureTokenSilently();
    return true;
  } catch {
    return false;
  }
}

export function signOutDrive() {
  _token = null;
  _tokenExpiry = 0;
}

export function clearFolderCache() {
  _folderCache.clear();
  saveFolderCache();
}

export async function getAccessToken() {
  if (_token && nowSec() < _tokenExpiry) return _token;
  try {
    await ensureTokenSilently();
    if (_token) return _token;
  } catch {}
  throw new Error("No valid token. Trigger sign-in from a user click.");
}

export function hasDriveToken() {
  const ok = !!_token && nowSec() < _tokenExpiry;
  if (DBG) log("hasDriveToken() ?", ok, ok ? `exp ${ts(_tokenExpiry)}` : "");
  return ok;
}

export function getCachedDriveRootId() {
  try {
    return localStorage.getItem(LS_DRIVE_ROOT_ID) || null;
  } catch {
    return null;
  }
}

export function setCachedDriveRootId(id) {
  try {
    if (id) localStorage.setItem(LS_DRIVE_ROOT_ID, id);
  } catch {}
}

/* ---------------- Drive fetch helpers ---------------- */

async function gFetch(url, opts = {}) {
  const token = await getAccessToken();
  if (DBG) log("fetch →", url);

  try {
    const method = String(opts?.method || "GET").toUpperCase();
    const s = String(url || "");
    if (method === "GET") {
      if (s.includes("/drive/v3/files") && s.includes("?q=")) incDriveRequest("list");
      else if (s.includes("/drive/v3/files")) incDriveRequest("get");
      else incDriveRequest("get");
    } else if (method === "POST") {
      incDriveRequest("create");
    } else if (method === "PATCH" || method === "PUT") {
      incDriveRequest("update");
    } else {
      incDriveRequest("get");
    }
  } catch {
    // ignore
  }

  const res = await fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    warn("Drive error", res.status, url, text);
    throw new Error(`Drive error ${res.status}: ${text || res.statusText}`);
  }
  return res;
}

async function fetchFileContent(fileId) {
  const key = String(fileId || "").trim();
  if (!key) throw new Error("fileId required");

  const now = Date.now();
  const cached = _fileCache.get(key);
  if (cached && now - cached.ts < FILE_TTL_MS) return cached.value;

  const pending = _fileInflight.get(key);
  if (pending) return pending;

  const flight = (async () => {
    try {
      const res = await gFetch(`https://www.googleapis.com/drive/v3/files/${key}?alt=media`);
      const json = await res.json();
      _fileCache.set(key, { ts: Date.now(), value: json });
      return json;
    } finally {
      _fileInflight.delete(key);
    }
  })();

  _fileInflight.set(key, flight);
  return flight;
}

async function gUpload(url, body, headers = {}, method = "POST") {
  const token = await getAccessToken();
  try {
    const m = String(method || "POST").toUpperCase();
    if (m === "POST") incDriveRequest("create");
    else if (m === "PATCH" || m === "PUT") incDriveRequest("update");
    else incDriveRequest("update");
  } catch {
    // ignore
  }
  const res = await fetch(url, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...headers },
    body,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    warn("Drive upload error", res.status, url, text);
    throw new Error(`Drive upload ${res.status}: ${text || res.statusText}`);
  }
  return res;
}

/* ---------------- Folder utilities ---------------- */

function qstr(str) {
  return String(str).replace(/'/g, "\\'");
}

async function findChildByName(parentId, name, mime) {
  const key = `${parentId}::${name}::${mime || "*"}`;
  const now = Date.now();

  const cached = _findCache.get(key);
  if (cached && now - cached.ts < FIND_TTL_MS) return cached.value;

  const pending = _findInflight.get(key);
  if (pending) return pending;

  const flight = (async () => {
    try {
      const q =
        `'${qstr(parentId)}' in parents and trashed=false and name='${qstr(name)}'` +
        (mime ? ` and mimeType='${qstr(mime)}'` : "");
      const url =
        "https://www.googleapis.com/drive/v3/files" +
        "?q=" + encodeURIComponent(q) +
        "&fields=files(id,name,mimeType,modifiedTime,parents)";
      const res = await gFetch(url);
      const json = await res.json();
      const found = (json.files && json.files[0]) || null;
      _findCache.set(key, { ts: Date.now(), value: found });
      return found;
    } finally {
      _findInflight.delete(key);
    }
  })();

  _findInflight.set(key, flight);
  return flight;
}

// ✅ FIX #3: Cached file ID lookup – prevents repeated Drive metadata queries
// Used by upsertJsonInFolder and other file operations
async function getFileIdByName(parentId, name) {
  const cacheKey = `${parentId}:${name}`;
  const now = Date.now();

  // Check cache (5 minute TTL)
  const cached = _fileIdCache.get(cacheKey);
  if (cached && now - cached.ts < FILE_ID_CACHE_TTL_MS) {
    if (DBG) log("getFileIdByName CACHE HIT", { parentId, name });
    incFileIdStat("cacheHit");
    return cached.id;
  }

  incFileIdStat("fetch");

  // Query Drive for file
  const file = await findChildByName(parentId, name, null);
  const fileId = file?.id || null;

  // Cache the result (even if null, to avoid repeated queries)
  _fileIdCache.set(cacheKey, { ts: now, id: fileId });

  if (DBG && fileId) log("getFileIdByName FETCHED", { parentId, name, fileId });
  return fileId;
}

async function createFolder(name, parentId) {
  const body = {
    name,
    mimeType: "application/vnd.google-apps.folder",
    parents: [parentId],
    appProperties: { mudo: "1" },
  };
  const res = await gFetch(
    "https://www.googleapis.com/drive/v3/files?fields=id,name,parents",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  return res.json();
}

export async function ensureFolderByPath(pathSegments) {
  const key = Array.isArray(pathSegments) ? pathSegments.join("/") : String(pathSegments);
  if (_folderCache.has(key)) {
    return _folderCache.get(key);
  }

  let parentId = "root";
  for (const name of pathSegments) {
    const existing = await findChildByName(
      parentId,
      name,
      "application/vnd.google-apps.folder"
    );
    if (existing) parentId = existing.id;
    else {
      const created = await createFolder(name, parentId);
      parentId = created.id;
    }
  }
  log("ensureFolderByPath", pathSegments, "→", parentId);
  _folderCache.set(key, parentId);
  saveFolderCache();
  return parentId;
}

/* ---------------- JSON read/write (scoped to /Mudo) ---------------- */

export async function writeJsonInFolder(folderId, fileName, jsonObj) {
  const metadata = { name: fileName, parents: [folderId], mimeType: "application/json" };
  const boundary = "mudo_boundary_" + Math.random().toString(36).slice(2);
  const body =
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n` +
    JSON.stringify(metadata) + "\r\n" +
    `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
    JSON.stringify(jsonObj) + "\r\n" +
    `--${boundary}--`;

  const res = await gUpload(
    "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,modifiedTime",
    body,
    { "Content-Type": `multipart/related; boundary=${boundary}` },
    "POST"
  );
  return res.json();
}

export async function upsertJsonInFolder(folderId, fileName, jsonObj) {
  // ✅ FIX #3: Use cached fileId lookup instead of repeated Drive queries
  const existingId = await getFileIdByName(folderId, fileName);
  if (!existingId) return writeJsonInFolder(folderId, fileName, jsonObj);

  const boundary = "mudo_boundary_" + Math.random().toString(36).slice(2);
  const metadata = { name: fileName, mimeType: "application/json" };
  const body =
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n` +
    JSON.stringify(metadata) + "\r\n" +
    `--${boundary}\r\nContent-Type: application/json\r\n\r\n` +
    JSON.stringify(jsonObj) + "\r\n" +
    `--${boundary}--`;

  const res = await gUpload(
    `https://www.googleapis.com/upload/drive/v3/files/${existingId}?uploadType=multipart&fields=id,name,modifiedTime`,
    body,
    { "Content-Type": `multipart/related; boundary=${boundary}` },
    "PATCH"
  );
  return res.json();
}

// ✅ FIX #3: Invalidate fileId cache on demand (call after explicit file operations)
export function invalidateFileIdCache(parentId, name) {
  if (parentId && name) {
    const key = `${parentId}:${name}`;
    _fileIdCache.delete(key);
    if (DBG) log("Invalidated fileId cache", { parentId, name });
  }
}

export async function readJsonFromFolder(folderId, fileName) {
  const existing = await findChildByName(folderId, fileName, null);
  if (!existing) return null;
  return fetchFileContent(existing.id);
}

export async function readBlobFromFolder(folderId, fileName) {
  const existing = await findChildByName(folderId, fileName, null);
  if (!existing) return null;
  const res = await gFetch(`https://www.googleapis.com/drive/v3/files/${existing.id}?alt=media`);
  return res.blob();
}

/* ---------------- Public helpers ---------------- */

export async function ensureMudoRoot() {
  return ensureFolderByPath(["Mudo"]);
}

export async function ensureMudoBackups(parentId) {
  if (parentId) {
    const existing = await findChildByName(
      parentId,
      "backups",
      "application/vnd.google-apps.folder"
    );
    if (existing) return existing.id;
    const created = await createFolder("backups", parentId);
    return created.id;
  }
  return ensureFolderByPath(["Mudo", "backups"]);
}

// Idempotent Drive linking with cached root id and token reuse
export async function ensureDriveLinked() {
  const cachedRoot = getCachedDriveRootId();
  const hasToken = hasDriveToken();
  log("ensureDriveLinked -> existing", { hasToken, driveRootFolderId: cachedRoot });

  try {
    await ensureGisReady();
  } catch (err) {
    warn("ensureDriveLinked: GIS not ready", err?.message || err);
    return { ok: false, reason: "gis_not_ready" };
  }

  if (!hasToken) {
    try {
      await ensureTokenSilently();
    } catch (err) {
      warn("ensureDriveLinked: silent token failed", err?.message || err);
    }
  }

  if (!hasDriveToken()) {
    log("ensureDriveLinked -> requesting user gesture");
    try {
      await requestTokenFromUserGesture();
    } catch (err) {
      warn("ensureDriveLinked: user cancelled or failed", err?.message || err);
      return { ok: false, reason: "user_cancelled" };
    }
  }

  let rootId = cachedRoot;
  if (!rootId) {
    log("ensureDriveLinked -> linking flow started");
    try {
      rootId = await ensureMudoRoot();
      setCachedDriveRootId(rootId);
      log("ensureDriveLinked -> folder chosen", { driveRootFolderId: rootId });
    } catch (err) {
      warn("ensureDriveLinked: ensureMudoRoot failed", err?.message || err);
      return { ok: false, reason: "link_failed" };
    }
  }

  return { ok: true, driveRootFolderId: rootId };
}

export async function healthCheck(rootId) {
  const ok = await upsertJsonInFolder(rootId, ".mudo.studio", {
    stamp: new Date().toISOString(),
    kind: "sentinel",
  });
  return !!ok?.id;
}

export async function getDriveUserInfo() {
  try {
    const token = await getAccessToken();
    const res = await fetch("https://www.googleapis.com/drive/v3/about?fields=user", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.user || null;
  } catch {
    return null;
  }
}

/* ---------------- Nice-to-have extras (safe) ---------------- */

export async function listFilesInFolder(folderId, { mimeType, pageSize = 50 } = {}) {
  const q =
    `'${qstr(folderId)}' in parents and trashed=false` +
    (mimeType ? ` and mimeType='${qstr(mimeType)}'` : "");
  const url =
    "https://www.googleapis.com/drive/v3/files" +
    "?q=" + encodeURIComponent(q) +
    `&pageSize=${pageSize}` +
    "&fields=files(id,name,mimeType,modifiedTime,size)";
  const res = await gFetch(url);
  const json = await res.json();
  return json.files || [];
}

export async function deleteFileInFolder(folderId, fileName) {
  const existing = await findChildByName(folderId, fileName, null);
  if (!existing) return false;
  const res = await gFetch(`https://www.googleapis.com/drive/v3/files/${existing.id}`, {
    method: "DELETE",
  });
  return res.ok || res.status === 204;
}

export async function upsertBlobInFolder(folderId, fileName, blob, mime = "application/octet-stream") {
  const existing = await findChildByName(folderId, fileName, null);
  const boundary = "mudo_boundary_" + Math.random().toString(36).slice(2);
  const meta = { name: fileName, mimeType: mime, parents: existing ? undefined : [folderId] };

  const metaPart =
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n` +
    JSON.stringify(meta) + "\r\n";
  const binPart =
    `--${boundary}\r\nContent-Type: ${mime}\r\n\r\n`;
  const end = `\r\n--${boundary}--`;

  const body = new Blob([metaPart, binPart, blob, end], { type: `multipart/related; boundary=${boundary}` });

  const url = existing
    ? `https://www.googleapis.com/upload/drive/v3/files/${existing.id}?uploadType=multipart&fields=id,name,modifiedTime`
    : "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,modifiedTime";

  const res = await gUpload(url, body, { "Content-Type": `multipart/related; boundary=${boundary}` }, existing ? "PATCH" : "POST");
  return res.json();
}
